
import React, { useEffect, useState } from 'react';
import Swal from '../librarianSwal';
import Barcode from 'react-barcode';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import '../lib-page-header.css';
import '../BookList.css';
import FilterSelect from '../components/FilterSelect';
import Pagination from '../components/Pagination';

import {
  getBookCopiesByLibrary,
  getErrorMessage
} from '../../api/api';

const LABEL_COLORS = [
  { strong: '#2563eb', pale: '#eff6ff' },
  { strong: '#7c3aed', pale: '#f5f3ff' },
  { strong: '#0e7490', pale: '#ecfeff' },
  { strong: '#15803d', pale: '#f0fdf4' },
  { strong: '#b45309', pale: '#fffbeb' },
  { strong: '#be123c', pale: '#fff1f2' },
  { strong: '#4338ca', pale: '#eef2ff' },
  { strong: '#0f766e', pale: '#f0fdfa' }
];

const labelColor = (key) => {
  const s = String(key ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return LABEL_COLORS[h % LABEL_COLORS.length];
};

const PAGE_SIZE = 10;

const BarcodePrint = () => {
  const libraryId = useActiveLibraryId();

  const [copies, setCopies] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);

  const fetchCopies = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const data = await getBookCopiesByLibrary(libraryId);
      setCopies(data || []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCopies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedIds(filteredCopies.map((item) => item.id));
  };

  const clearAll = () => {
    setSelectedIds([]);
  };

  const handlePrint = () => {
    if (selectedIds.length === 0) {
      Swal.fire('Error', 'Please select copies to print', 'error');
      return;
    }

    window.print();
  };

  const filteredCopies = copies.filter((copy) => {
    if (statusFilter && String(copy.availability_status || '').toLowerCase() !== statusFilter) return false;
    const keyword = search.toLowerCase();
    if (!keyword) return true;
    return (
      String(copy.title || copy.book_title || '').toLowerCase().includes(keyword) ||
      String(copy.accession_no || '').toLowerCase().includes(keyword) ||
      String(copy.barcode || '').toLowerCase().includes(keyword) ||
      String(copy.author_name || '').toLowerCase().includes(keyword)
    );
  });

  const selectedCopies = copies.filter((copy) => selectedIds.includes(copy.id));

  const allSelected =
    filteredCopies.length > 0 &&
    filteredCopies.every((copy) => selectedIds.includes(copy.id));

  const totalPages = Math.max(1, Math.ceil(filteredCopies.length / PAGE_SIZE));
  const paginatedCopies = filteredCopies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="container-fluid mt-3">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 8mm;
            }

            body * {
              visibility: hidden;
            }

            .print-area, .print-area * {
              visibility: visible;
            }

            .print-area {
              position: absolute;
              /* Anchor to BOTH edges so the grid fills exactly the printable
                 area (inside the @page margins) — using width:100% instead can
                 resolve to the full page width and push the 3rd column off the
                 right edge. */
              left: 0;
              right: 0;
              top: 0;
              width: auto;
              box-sizing: border-box;
              display: grid;
              /* minmax(0, 1fr) lets columns shrink below the barcode's
                 intrinsic width so all 3 fit — otherwise the 3rd is clipped. */
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 4mm;
              justify-items: stretch;
            }

            .no-print {
              display: none !important;
            }

            .barcode-label {
              width: 100%;
              max-width: 100%;
              min-width: 0;
              box-sizing: border-box;
              margin: 0;
              page-break-inside: avoid;
              break-inside: avoid;
            }

            .barcode-box svg {
              max-width: 100%;
              height: auto;
            }
          }

          .barcode-label, .barcode-label * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .barcode-label {
            width: 230px;
            min-height: 110px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            overflow: hidden;
            margin: 8px;
            display: inline-block;
            text-align: center;
            vertical-align: top;
            box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
          }

          .barcode-head {
            color: #fff;
            font-size: 12px;
            font-weight: 700;
            padding: 7px 8px;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .barcode-box {
            padding: 8px 0 4px;
            margin: 8px 8px 0;
            background: #fff;
            border-radius: 6px;
          }

          .barcode-box svg {
            max-width: 100%;
            height: auto;
          }

          .barcode-meta {
            font-size: 12px;
            font-weight: 700;
            padding: 4px 8px 10px;
            color: #1e293b;
            line-height: 1.5;
          }
        `}
      </style>

      <div className="no-print">
        <div className="lib-page-header">
          <div className="lib-page-header__left">
            <div className="lib-page-header__icon"><i className="fas fa-barcode" /></div>
            <div>
              <h3 className="lib-page-header__title">Barcode Print</h3>
              <div className="lib-page-header__subtitle">Select book copies and print barcode labels</div>
            </div>
          </div>
          <div className="lib-page-header__actions">
            <button className="btn btn-primary" onClick={handlePrint}>Print Selected</button>
          </div>
        </div>

        <div className="card shadow-sm mb-3 table-full-card">
          <div className="card-body">
            <div className="book-filter-row mb-3">
              <input
                type="text"
                className="book-search-input"
                placeholder="Book title, accession no, barcode, author..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <FilterSelect
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'available', label: 'Available' },
                  { value: 'issued', label: 'Issued' },
                  { value: 'discarded', label: 'Discarded' },
                ]}
              />

              <button
                className="book-reset-btn"
                onClick={() => { setSearch(''); setStatusFilter(''); clearAll(); }}
              >
                Reset
              </button>
            </div>

            {showLoader ? (
              <Loader />
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => (e.target.checked ? selectAll() : clearAll())}
                        />
                        {/* <span className="ms-1">Select</span> */}
                      </th>
                      <th>#</th>
                      <th>Book</th>
                      <th>Accession No</th>
                      <th>Barcode</th>
                      <th>Copy No</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedCopies.map((copy, index) => (
                      <tr key={copy.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(copy.id)}
                            onChange={() => toggleSelect(copy.id)}
                          />
                        </td>
                        <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                        <td>{copy.title || copy.book_title || '-'}</td>
                        <td>{copy.accession_no || '-'}</td>
                        <td>{copy.barcode || '-'}</td>
                        <td>{copy.copy_number || '-'}</td>
                        <td>{copy.availability_status || '-'}</td>
                      </tr>
                    ))}

                    {filteredCopies.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          No copies found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={filteredCopies.length}
                  itemLabel="copies"
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="print-area">
        {selectedCopies.map((copy) => {
          const c = labelColor(copy.book_id ?? copy.title ?? copy.book_title);
          const barcodeValue = String(copy.barcode || copy.accession_no || copy.id);
          return (
            <div className="barcode-label" key={copy.id} style={{ background: c.pale, borderColor: c.strong }}>
              <div className="barcode-head" style={{ background: c.strong }}>
                {copy.title || copy.book_title || 'Library Book'}
              </div>

              <div className="barcode-box">
                <Barcode
                  value={barcodeValue}
                  format="CODE128"
                  width={1.6}
                  height={50}
                  displayValue={false}
                  margin={0}
                  background="transparent"
                />
              </div>

              <div className="barcode-meta">
                Barcode: {barcodeValue}<br />
                Accession: {copy.accession_no || '-'} &middot; Copy: {copy.copy_number || '-'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BarcodePrint;